import React, { useEffect, useMemo, useState } from 'react'
import { Heading, Button, Text } from '../ui'

type RunStatus = 'success' | 'failed' | 'cancelled' | null

interface Process {
  id: string
  key: string
  name: string
  description?: string | null
  is_active: boolean
}

interface ProcessSchedule {
  id: string
  process_id: string
  description: string | null
  enabled: boolean
  weekdays?: any
  hours?: any
  next_run_at: string | null
  last_run_at?: string | null
  last_run_status?: RunStatus
  json_parameters?: any
}

interface CalendarModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  schedules: ProcessSchedule[]
  processes: Process[]
}

function parseNumericArrayText(text?: string | null): number[] {
  if (!text || typeof text !== 'string') return []
  const matches = text.match(/\d+/g)
  return matches ? matches.map((n) => Number(n)) : []
}

function formatDateKey(d: Date): string {
  const y = d.getFullYear()
  const m = (d.getMonth() + 1).toString().padStart(2, '0')
  const day = d.getDate().toString().padStart(2, '0')
  return `${y}-${m}-${day}`
}

function monthStart(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), 1)
}

function monthEnd(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth() + 1, 0)
}

function addDays(date: Date, days: number): Date {
  const d = new Date(date)
  d.setDate(d.getDate() + days)
  return d
}

const MonthGrid: React.FC<{
  title: string
  start: Date
  end: Date
  itemsByDate: Map<string, { time: number; label: string; id: string }[]>
  onOpenDay: (dateKey: string) => void
}> = ({ title, start, end, itemsByDate, onOpenDay }) => {
  const days: Date[] = useMemo(() => {
    const list: Date[] = []
    for (let d = new Date(start); d <= end; d = addDays(d, 1)) list.push(new Date(d))
    return list
  }, [start, end])

  const leadingBlanks = start.getDay() // 0..6 (Dom..Sáb)
  const todayKey = useMemo(() => formatDateKey(new Date()), [])

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
      <div className="flex items-center justify-between mb-3">
        <Heading as="h4" size="2xl">{title}</Heading>
      </div>
      <div className="grid grid-cols-7 gap-2 text-xs text-gray-500 dark:text-gray-400 mb-2">
        <div>Dom</div><div>Seg</div><div>Ter</div><div>Qua</div><div>Qui</div><div>Sex</div><div>Sáb</div>
      </div>
      <div className="grid grid-cols-7 gap-2">
        {Array.from({ length: leadingBlanks }).map((_, i) => (
          <div key={`blank-${i}`} className="p-2" />
        ))}
        {days.map((d) => {
          const key = formatDateKey(d)
          const items = itemsByDate.get(key) || []
          const isToday = key === todayKey
          return (
            <div
              key={key}
              className={
                `border rounded p-2 min-h-[90px] flex flex-col cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 ` +
                (isToday
                  ? 'border-blue-500 ring-2 ring-blue-500 bg-blue-50 dark:border-blue-400 dark:ring-blue-400 dark:bg-blue-900/30'
                  : 'border-gray-200 dark:border-gray-700')
              }
              onClick={() => onOpenDay(key)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') onOpenDay(key) }}
            >
              <div className={`text-sm font-medium ${isToday ? 'text-blue-700 dark:text-blue-200' : 'text-gray-700 dark:text-gray-200'}`}>{d.getDate()}</div>
              <ul className="mt-1 space-y-0.5">
                {items.slice(0, 3).map((ev) => (
                  <li key={ev.id + '-' + ev.time} className="truncate text-xs text-gray-700 dark:text-gray-300">
                    {ev.time.toString().padStart(2, '0')}:00 — {ev.label}
                  </li>
                ))}
                {items.length > 3 && (
                  <li className="text-xs text-gray-500 dark:text-gray-400">+{items.length - 3} mais</li>
                )}
              </ul>
            </div>
          )
        })}
      </div>
    </div>
  )
}

const CalendarModal: React.FC<CalendarModalProps> = ({ open, onOpenChange, schedules, processes }) => {
  const processById = useMemo(() => {
    const m = new Map<string, Process>()
    processes.forEach(p => m.set(p.id, p))
    return m
  }, [processes])

  const [viewMonth, setViewMonth] = useState<Date>(() => monthStart(new Date()))
  const [dayModalDateKey, setDayModalDateKey] = useState<string | null>(null)
  const startRange = viewMonth
  const endRange = monthEnd(viewMonth)

  const itemsByDate = useMemo(() => {
    const map = new Map<string, { time: number; label: string; id: string }[]>()

    const allDates: Date[] = []
    for (let d = new Date(startRange); d <= endRange; d = addDays(d, 1)) allDates.push(new Date(d))

    const parsedSchedules = schedules.map((s) => {
      const weekdays = Array.isArray(s.weekdays) ? (s.weekdays as number[]) : parseNumericArrayText(s.weekdays)
      const hours = Array.isArray(s.hours) ? (s.hours as number[]) : parseNumericArrayText(s.hours)
      return { s, weekdays, hours }
    })

    for (const date of allDates) {
      const dow = date.getDay()
      for (const { s, weekdays, hours } of parsedSchedules) {
        if (!s.enabled) continue
        if (!weekdays.includes(dow)) continue
        const proc = processById.get(s.process_id)
        const label = s.description ? s.description : (proc?.name || proc?.key || 'Agendamento')
        for (const h of hours) {
          const key = formatDateKey(date)
          if (!map.has(key)) map.set(key, [])
          map.get(key)!.push({ time: h, label, id: s.id })
        }
      }
    }

    // Ordenar cada dia por hora
    map.forEach((arr, k) => {
      arr.sort((a: { time: number; label: string; id: string }, b: { time: number; label: string; id: string }) => a.time - b.time)
      map.set(k, arr)
    })

    return map
  }, [schedules, processById, startRange, endRange])

  // Sempre que abrir o modal, voltar para o mês atual
  useEffect(() => {
    if (open) {
      setViewMonth(monthStart(new Date()))
    }
  }, [open])

  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (dayModalDateKey) {
          setDayModalDateKey(null)
        } else {
          onOpenChange(false)
        }
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, onOpenChange, dayModalDateKey])

  if (!open) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={() => onOpenChange(false)}>
      <div className="bg-white dark:bg-gray-800 rounded-lg w-full max-w-6xl max-h-[90vh] overflow-y-auto p-6" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={() => setViewMonth(prev => monthStart(new Date(prev.getFullYear(), prev.getMonth() - 1, 1)))}>Anterior</Button>
            <Heading as="h3" size="2xl">Calendário de agendamentos</Heading>
            <Button variant="outline" onClick={() => setViewMonth(prev => monthStart(new Date(prev.getFullYear(), prev.getMonth() + 1, 1)))}>Próximo</Button>
          </div>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Fechar</Button>
        </div>
        
        <div className="grid grid-cols-1 gap-6">
          <MonthGrid
            title={viewMonth.toLocaleDateString(undefined, { month: 'long', year: 'numeric' })}
            start={startRange}
            end={endRange}
            itemsByDate={itemsByDate}
            onOpenDay={(dateKey) => setDayModalDateKey(dateKey)}
          />
        </div>
        {dayModalDateKey && (
          <div className="fixed inset-0 flex items-center justify-center" onClick={() => setDayModalDateKey(null)}>
            <div className="bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg w-full max-w-md max-h-[70vh] overflow-y-auto p-5 shadow-xl" onClick={e => e.stopPropagation()}>
              <div className="flex items-center justify-between mb-3">
                <Heading as="h4" size="2xl">
                  {(() => { const [y, m, d] = dayModalDateKey.split('-').map(Number); return new Date(y, m - 1, d).toLocaleDateString(undefined, { day: '2-digit', month: 'long', year: 'numeric' }) })()}
                </Heading>
                <Button variant="ghost" onClick={() => setDayModalDateKey(null)}>Fechar</Button>
              </div>
              <div className="space-y-2">
                {(() => {
                  const items = itemsByDate.get(dayModalDateKey) || []
                  if (items.length === 0) return <Text size="sm" color="muted">Sem agendamentos</Text>
                  return (
                    <ul className="divide-y divide-gray-200 dark:divide-gray-700">
                      {items.map(ev => (
                        <li key={ev.id + '-' + ev.time} className="py-2 text-sm text-gray-800 dark:text-gray-100 break-words">
                          <span className="font-mono mr-2">{ev.time.toString().padStart(2, '0')}:00</span>
                          <span>{ev.label}</span>
                        </li>
                      ))}
                    </ul>
                  )
                })()}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default CalendarModal


